import { loc } from '../../data/cards'
import { birthIsoRange } from '../../../../shared/birth.ts'
import { RichText } from '../../components/RichText'
import type { Lang } from '../../data/cards'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMainButton } from '../../components/MainButton'
import { sunSignOf } from '../../../../shared/astro.ts'
import { axisDetail, axisLabel, compatibility } from '../../../../shared/compat.ts'
import { signById, SIGNS } from '../../data/signs'
import { api, ApiError } from '../../lib/api'
import { haptic } from '../../lib/telegram'
import { FREE_LIMIT_PER_DAY, useSirr } from '../../store'

/** Границы поля даты. Считаются один раз: за сеанс год не меняется. */
const BIRTH_RANGE = birthIsoRange()

export type Text =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; text: string }
  | { status: 'failed' }
  /* Дневной лимит исчерпан. Отдельно от 'failed' намеренно: предлагать
     «Повторить» бессмысленно, запрос будет отклоняться до завтра. */
  | { status: 'limit' }

function toInput(d: { year: number; month: number; day: number } | null): string {
  if (!d) return ''
  return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`
}

function fromInput(v: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (!m) return null
  const year = Number(m[1])
  if (year < 1900 || year > 2100) return null
  return { year, month: Number(m[2]), day: Number(m[3]) }
}

export function Match() {
  const { profile, lang, t, update } = useSirr()

  const [you, setYou] = useState(() => toInput(profile.birth))
  const [partner, setPartner] = useState(() => toInput(profile.partner))
  const [shown, setShown] = useState(false)
  const [text, setText] = useState<Text>({ status: 'idle' })

  const yourDate = fromInput(you)
  const partnerDate = fromInput(partner)
  const ready = Boolean(yourDate && partnerDate)

  const result = useMemo(() => {
    if (!yourDate || !partnerDate) return null
    const a = sunSignOf(yourDate.year, yourDate.month, yourDate.day)
    const b = sunSignOf(partnerDate.year, partnerDate.month, partnerDate.day)
    return compatibility(a, b)
  }, [yourDate, partnerDate])

  // Даты меняются — прежний расчёт больше не относится к делу.
  useEffect(() => {
    setShown(false)
    setText({ status: 'idle' })
  }, [you, partner])

  const requestText = useCallback(async () => {
    if (!yourDate || !partnerDate) return
    setText({ status: 'loading' })
    try {
      const res = await api.astro({ kind: 'compat', lang, a: yourDate, b: partnerDate, goals: profile.goals })
      // Счётчик раскладов Таро здесь не трогаем: у астрологии он свой,
      // и запись сюда стирала бы чужой остаток.
      setText({ status: 'ok', text: res.text })
      haptic.notify('success')
    } catch (err) {
      setText({ status: err instanceof ApiError && err.code === 'limit_reached' ? 'limit' : 'failed' })
    }
  }, [yourDate, partnerDate, lang, profile.goals])

  const calculate = () => {
    if (!yourDate || !partnerDate) return
    haptic.impact('medium')
    setShown(true)
    // Даты пригодятся при следующем заходе.
    // Свою дату запоминаем, чтобы не вводить её заново в натальной карте.
    // Время не трогаем: здесь его никто не спрашивал.
    update({
      partner: partnerDate,
      birth: profile.birth
        ? { ...profile.birth, ...yourDate }
        : { ...yourDate, hour: null, minute: null, place: null, timeUnknown: false },
    })
    void requestText()
  }

  useMainButton({
    label: shown ? t('recalculate') : t('calculate'),
    disabled: !ready,
    loading: text.status === 'loading',
    onClick: calculate,
  })

  return (
    <>
      <div className="stack" style={{ marginBottom: 18 }}>
        <Field label={t('yourBirth')} value={you} onChange={setYou} />
        <Field label={t('partnerBirth')} value={partner} onChange={setPartner} />
      </div>

      {!ready && (
        <p style={{ textAlign: 'center', color: 'var(--ink-faint)', fontSize: 13 }}>{t('fillBoth')}</p>
      )}

      {ready && result && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
          <SignCard title={t('you')} sign={result.signA} lang={lang} />
          <SignCard title={t('partner')} sign={result.signB} lang={lang} />
        </div>
      )}

      {shown && result && (
        <div style={{ animation: 'fu .6s both' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 18 }}>
            <div
              style={{
                position: 'relative',
                width: 140,
                height: 140,
                borderRadius: '50%',
                background: `conic-gradient(var(--gold) 0deg ${result.score * 3.6}deg, rgba(200,162,78,.14) ${result.score * 3.6}deg 360deg)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'var(--gold-glow)',
              }}
            >
              <div
                style={{
                  width: 112,
                  height: 112,
                  borderRadius: '50%',
                  background: 'var(--bg-velvet)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(200,162,78,.3)',
                }}
              >
                <div style={{ fontFamily: 'var(--serif)', fontSize: 38 }}>{result.score}%</div>
                <div className="eyebrow" style={{ fontSize: 10, letterSpacing: 1 }}>{t('harmony')}</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {result.axes.map((axis) => (
              <div key={axis.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#c9bcd8', marginBottom: 4 }}>
                  <span>
                    {axisLabel(axis, lang)}
                    <span style={{ color: 'var(--ink-faint)' }}> · {axisDetail(axis, lang)}</span>
                  </span>
                  <span style={{ color: 'var(--gold)' }}>{axis.value}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 4, background: 'rgba(200,162,78,.12)' }}>
                  <div style={{ height: '100%', borderRadius: 4, width: `${axis.value}%`, background: 'var(--gold-gradient)' }} />
                </div>
              </div>
            ))}
          </div>

          <InterpretationBlock text={text} onRetry={() => void requestText()} />
        </div>
      )}
    </>
  )
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label style={{ display: 'block', border: '1px solid var(--line)', borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 4 }}>{label}</div>
      <input
        type="date"
        value={value}
        // Границы общие с нативом и сервером: до этого веб разрешал дату
        // рождения в будущем, вплоть до 2100 года.
        min={BIRTH_RANGE.min}
        max={BIRTH_RANGE.max}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 0,
          outline: 'none',
          color: 'var(--ink)',
          fontFamily: 'var(--serif)',
          fontStyle: 'italic',
          fontSize: 17,
          padding: 0,
        }}
      />
    </label>
  )
}

function SignCard({ title, sign, lang }: { title: string; sign: number; lang: Lang }) {
  const def = signById(SIGNS[sign].id)
  return (
    <div style={{ flex: 1, border: '1px solid var(--line)', borderRadius: 14, padding: 14 }}>
      <div style={{ fontSize: 11, color: 'var(--ink-muted)' }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <span style={{ fontSize: 22, color: 'var(--gold)' }}>{def.glyph}</span>
        <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 17 }}>
          {loc(def, lang)}
        </span>
      </div>
    </div>
  )
}

export function InterpretationBlock({ text, onRetry }: { text: Text; onRetry: () => void }) {
  const { t } = useSirr()
  const retried = useRef(false)
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 16,
        padding: 18,
        background: 'linear-gradient(180deg, rgba(45,26,74,.45), rgba(18,10,31,.3))',
      }}
    >
      <div className="eyebrow" style={{ marginBottom: 10 }}>
        {text.status === 'loading' ? t('readingAstro') : t('interpretation')}
      </div>

      {text.status === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {[100, 94, 80].map((w) => (
            <div key={w} className="shimmer" style={{ width: `${w}%` }} />
          ))}
        </div>
      )}

      {text.status === 'ok' && (
        <RichText text={text.text} style={{ fontSize: 17, color: '#e2d6ef' }} />
      )}

      {text.status === 'limit' && (
        <>
          <p style={{ fontSize: 14, color: 'var(--ink-soft)', margin: 0, lineHeight: 1.5 }}>
            {t('limitTitle')}
          </p>
          <p style={{ fontSize: 13, color: 'var(--ink-muted)', margin: '6px 0 0', lineHeight: 1.5 }}>
            {t('limitBody').replace('%n', String(FREE_LIMIT_PER_DAY))}
          </p>
        </>
      )}

      {text.status === 'failed' && (
        <>
          <p style={{ fontSize: 13, color: 'var(--ink-muted)', margin: 0, lineHeight: 1.5 }}>{t('astroFailed')}</p>
          <button
            type="button"
            className="pick"
            style={{ marginTop: 10, padding: 11, textAlign: 'center', fontSize: 13 }}
            onClick={() => {
              retried.current = true
              haptic.impact('light')
              onRetry()
            }}
          >
            {t('retry')}
          </button>
        </>
      )}

      <p style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 14, marginBottom: 0, lineHeight: 1.45 }}>
        {t('astroNote')}
      </p>
    </div>
  )
}
